// @ts-check
import { InlineMath, BlockMath } from "@tiptap/extension-mathematics"
import { InputRule } from "@tiptap/core"
import katex from "katex"

/**
 * Builds a Tiptap node view that renders KaTeX by default and swaps to a raw
 * LaTeX-source input on click; committing (blur / Enter) re-renders the math.
 * @param {boolean} isInline
 */
function createEditableMathNodeView(isInline) {
  return ({ node, editor, getPos }) => {
    const dom = globalThis.document.createElement(isInline ? "span" : "div")
    dom.className = isInline
      ? "inline-block px-1 rounded bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/40 cursor-text align-baseline"
      : "block my-2 px-3 py-2 rounded-md bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/40 cursor-text"
    // Tell ProseMirror this subtree is opaque: don't intercept keystrokes/DOM
    // mutations happening inside our own <input>/<textarea>, otherwise typing
    // gets swallowed/reset by the editor's own contentEditable handling.
    dom.contentEditable = "false"

    let currentNode = node

    function renderView() {
      dom.innerHTML = ""
      try {
        katex.render(currentNode.attrs.latex || "", dom, { throwOnError: false, displayMode: !isInline })
      } catch {
        dom.textContent = currentNode.attrs.latex || ""
      }
    }

    function commit(value) {
      const pos = getPos()
      if (pos == null) return
      editor.view.dispatch(
        editor.state.tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, latex: value })
      )
      renderView()
    }

    function showEditor() {
      dom.innerHTML = ""
      const input = globalThis.document.createElement(isInline ? "input" : "textarea")
      input.value = currentNode.attrs.latex || ""
      input.className = isInline
        ? "font-mono text-sm outline-none bg-transparent min-w-[3ch]"
        : "w-full bg-transparent px-0 py-0 font-mono text-sm outline-none resize-y"
      if (isInline) input.size = Math.max(3, input.value.length)
      dom.appendChild(input)
      input.focus()
      input.select?.()

      input.addEventListener("blur", () => commit(input.value))
      if (isInline) {
        input.addEventListener("keydown", (e) => {
          if (e.key === "Enter") { e.preventDefault(); input.blur() }
        })
      }
    }

    dom.addEventListener("click", (e) => {
      if (dom.querySelector("input, textarea")) return
      e.preventDefault()
      e.stopPropagation()
      showEditor()
    })

    renderView()

    return {
      dom,
      update(updatedNode) {
        if (updatedNode.type !== node.type) return false
        currentNode = updatedNode
        if (!dom.querySelector("input, textarea")) renderView()
        return true
      },
      // Let native input/textarea events (typing, selection, etc.) inside our
      // own dom be handled by the browser, not intercepted as editor events.
      stopEvent() {
        return true
      },
      // Don't let ProseMirror's DOM-mutation observer try to reconcile
      // changes we make to our own dom (swapping between render/edit modes).
      ignoreMutation() {
        return true
      },
    }
  }
}

function escapeHtmlAttr(str) {
  return str
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
}

/** markdown-it plugin recognizing `$...$` (inline) and `$$ ... $$` (block) LaTeX syntax. */
function mathMarkdownItPlugin(md) {
  md.block.ruler.before("fence", "math_block", (state, startLine, endLine, silent) => {
    const pos = state.bMarks[startLine] + state.tShift[startLine]
    const max = state.eMarks[startLine]
    const firstLine = state.src.slice(pos, max).trim()
    if (!firstLine.startsWith("$$")) return false

    let latex = ""
    let nextLine = startLine
    let found = false

    if (firstLine.length > 2 && firstLine.endsWith("$$")) {
      latex = firstLine.slice(2, -2)
      found = true
      nextLine = startLine + 1
    } else {
      latex = firstLine.slice(2)
      for (let i = startLine + 1; i < endLine; i++) {
        const p = state.bMarks[i] + state.tShift[i]
        const m = state.eMarks[i]
        const line = state.src.slice(p, m)
        if (line.trim().endsWith("$$")) {
          latex += "\n" + line.trim().slice(0, -2)
          nextLine = i + 1
          found = true
          break
        }
        latex += "\n" + line
      }
    }

    if (!found) return false
    if (silent) return true

    state.line = nextLine
    const token = state.push("html_block", "", 0)
    token.content = `<div data-type="block-math" data-latex="${escapeHtmlAttr(latex.trim())}"></div>\n`
    token.map = [startLine, nextLine]
    return true
  })

  md.inline.ruler.before("escape", "math_inline", (state, silent) => {
    const start = state.pos
    if (state.src[start] !== "$" || state.src[start + 1] === "$") return false

    const match = state.src.slice(start + 1).match(/^([^$\n]+?)\$/)
    if (!match) return false

    if (!silent) {
      const token = state.push("html_inline", "", 0)
      token.content = `<span data-type="inline-math" data-latex="${escapeHtmlAttr(match[1])}"></span>`
    }
    state.pos = start + 1 + match[0].length
    return true
  })
}

const MarkdownBlockMath = BlockMath.extend({
  addNodeView() {
    return createEditableMathNodeView(false)
  },
  addInputRules() {
    return [
      new InputRule({
        find: /(?:^|\n)\$\$([^$\n]+)\$\$$/,
        handler: ({ state, range, match }) => {
          const [, latex] = match
          state.tr.replaceWith(range.from, range.to, this.type.create({ latex }))
        },
      }),
    ]
  },
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(state, node) {
          state.write(`$$\n${node.attrs.latex}\n$$`)
          state.closeBlock(node)
        },
        parse: {
          setup(markdownit) {
            markdownit.use(mathMarkdownItPlugin)
          },
        },
      },
    }
  },
})

const MarkdownInlineMath = InlineMath.extend({
  addNodeView() {
    return createEditableMathNodeView(true)
  },
  addInputRules() {
    return [
      new InputRule({
        find: /(?<!\$)\$([^$\n]+)\$$/,
        handler: ({ state, range, match }) => {
          const [, latex] = match
          state.tr.replaceWith(range.from, range.to, this.type.create({ latex }))
        },
      }),
    ]
  },
  addStorage() {
    return {
      ...this.parent?.(),
      markdown: {
        serialize(state, node) {
          state.write(`$${node.attrs.latex}$`)
        },
        parse: {
          setup(markdownit) {
            markdownit.use(mathMarkdownItPlugin)
          },
        },
      },
    }
  },
})

export { MarkdownBlockMath, MarkdownInlineMath }
